import { FieldOptions } from './fieldOptions.class'
import { PossibleValue } from './possibleValue.class'
import { Layout } from '../layout/layout.class'

import type { ICheckers, IFieldDefaultValue, IListener, IServerField } from '@/types/serverField.type'

import { CHECKER_TYPES } from '@/api/field.constants'
import { dispatchFieldEvent } from '@/components/FieldsList/utils'
import { EnumEventTypes } from '@/constants/eventTypes.constants'
import { IFilterValueType } from '@/types/sections.type'
import { IGridPlacement } from '@/types/serverGrid.type'
import { EmailChecker } from '@/utils/emailChecker'
import { isEqual } from '@/utils/isEqual'
import { NumberChecker } from '@/utils/numberChecker'
import { PasswordChecker } from '@/utils/passwordChecker'

const passwordChecker = new PasswordChecker()
const numberChecker = new NumberChecker()
const emailChecker = new EmailChecker()

const getValidationRawValue = (value: unknown): unknown => {
  if (typeof value !== 'object' || value === null) return value
  if ('value' in value) return value.value
  if ('Value' in value) return value.Value
  if ('Uuid' in value) return value.Uuid

  return value
}

const isNonSelectableValue = (value: unknown, nonSelectableValue: unknown): boolean => {
  if (nonSelectableValue === undefined) return false

  if (Array.isArray(value)) {
    return value.length > 0 && value.every(item => getValidationRawValue(item) === nonSelectableValue)
  }

  return getValidationRawValue(value) === nonSelectableValue
}

export abstract class BaseFieldClass {
  type: string
  name: string
  title: string
  filterOperator: string
  initialValue: unknown
  fieldValue: unknown
  viewName: string
  defaultValue: IFieldDefaultValue
  possibleValues: PossibleValue[]
  layout: Layout | null
  viewLayout: Layout
  gridPlacement: IGridPlacement | null
  widgetType: string
  WidgetName: string
  checkers: ICheckers | null
  required: boolean
  readonly: boolean
  weight: number
  listeners: IListener[] | null
  mask: string
  adapterName: string
  refreshAdapter: string
  refreshURL: string
  onChangeConfirm: boolean
  onChangeConfirmText: string
  options: FieldOptions
  multipleValues: boolean
  errorMessage: string = ''
  visible: boolean
  description: string
  descriptionDisabled: string
  _possibleValues?: unknown[] = []
  _values?: any[]
  _disabled: boolean = false
  _uid: number | undefined

  constructor (field: IServerField, ctx?: { uid?: number }) {
    this._uid = ctx?.uid
    this.type = field.Type || ''
    this.widgetType = field.WidgetType || ''
    this.WidgetName = ('WidgetName' in field) ? field.WidgetName : ''
    this.checkers = field.Checkers
    this.name = field.Name || ''
    this.title = field.Title || ''
    this.multipleValues = field.MultipleValues
    this.defaultValue = field.DefaultValue
    this.possibleValues = field.PossibleValues
      ? Object.values(field.PossibleValues).map(v => new PossibleValue(v))
      : []
    this.viewName = ('ViewName' in field) ? field.ViewName || field.WidgetName : ''
    this.layout = field.Layout ? new Layout(field.Layout) : null
    this.viewLayout = new Layout(field.VLayout)
    this.gridPlacement = field.GridPlacement || null
    this.required = field.Required
    this.filterOperator = field.FilterOperator || ''
    this.readonly = field.ReadOnly
    this._disabled = this.readonly
    this.weight = field.Weight
    this.listeners = field.Listeners
    this.mask = field.Mask || ''
    this.adapterName = field.AdapterName || ''
    this.refreshAdapter = field.RefreshAdapter || ''
    this.refreshURL = field.RefreshURL || ''
    this.onChangeConfirm = field.OnChangeConfirm === true
    this.onChangeConfirmText = field.OnChangeConfirmText || ''
    this.options = new FieldOptions(field.Options)
    this.visible = field.Visible
    this.description = field.Description
    this.descriptionDisabled = field.DescriptionDisabled
  }

  set value (value: unknown) {
    this.fieldValue = value
    this.checkValue()
  }

  get value () {
    return this.fieldValue
  }

  // Возвращает актуальное значение поля для клиентских реакций.
  // Отдельные типы полей могут хранить редактируемое значение вне fieldValue.
  getCurrentValue (): any {
    return this.value
  }

  get currentDescription (): string {
    return this._disabled
      ? this.descriptionDisabled
      : this.description
  }

  abstract prepareValue(value: unknown): unknown
  abstract getValue(): Record<string, unknown>
  abstract appendToFormData(formData: FormData): void
  abstract getFilterValue(): IFilterValueType['Value']
  getValueAsText (): string {
    if (this.value === null || this.value === undefined) return ''
    return String(this.value)
  }

  /**
   * Подготавливает значение поля для JSON-запроса в формате FieldRequestRecord.
   */
  valueToServer (): unknown {
    const values = this.getValue()

    if (Object.prototype.hasOwnProperty.call(values, this.name)) {
      return values[this.name] ?? null
    }

    const prefix = `${this.name}__`
    const result: Record<string, unknown> = {}

    Object.entries(values).forEach(([name, value]) => {
      if (name.startsWith(prefix)) {
        result[name.slice(prefix.length)] = value
      }
    })

    return Object.keys(result).length ? result : null
  }

  setFilterValue (value: unknown): void {
    this.fieldValue = value
  }

  hasValue (): boolean {
    if (isNonSelectableValue(this.value, this.options.nonSelectableValue)) return false
    if (typeof this.value === 'number') return this.value >= 0
    if (Array.isArray(this.value)) return this.value.length > 0
    return !!this.value
  }

  checkValue () {
    // Проверку скрытых и невидимых полей пропускаем
    if (!this.visible || this.type === 'hidden') return true

    this.errorMessage = ''

    if (this.required && (!this.hasValue() || this.isEmpty())) {
      this.errorMessage = isNonSelectableValue(this.value, this.options.nonSelectableValue)
        ? 'Необходимо выбрать значение.'
        : 'Поле обязательно для заполнения.'
      return false
    }

    if (!this.required && !this.hasValue()) {
      return true
    }

    Object.keys(this.checkers || {}).forEach(checker => {
      const param = (this.checkers as any)[checker]
      switch (checker) {
        case CHECKER_TYPES.digit:
          if (!passwordChecker.checkDigit(this.value as string)) { this.errorMessage += 'Добавьте как минимум одну цифру.' }
          break
        case CHECKER_TYPES.digitsAll:
          if (!passwordChecker.checkDigitsAll(this.value as string)) { this.errorMessage += 'Нужно указать только цифры.' }
          break
        case CHECKER_TYPES.email:
          if (!emailChecker.checkEmailsList(this.value as string, parseInt(param))) { this.errorMessage += 'Введен некорректный адрес электронной почты.' }
          break
        case CHECKER_TYPES.lengthMax:
          if (!passwordChecker.checkMaxLength(this.value, parseInt(param))) { this.errorMessage += `Количество символов должно быть меньше или равно ${param}.` }
          break
        case CHECKER_TYPES.lengthMin:
          if (!passwordChecker.checkMinLength(this.value, parseInt(param))) { this.errorMessage += `Количество символов должно быть больше или равно ${param}.` }
          break
        case CHECKER_TYPES.lowerCaseLatinSymbol:
          if (!passwordChecker.checkAZLow(this.value as string)) { this.errorMessage += 'Должен быть как минимум 1 символ a-z в нижнем регистре.' }
          break
        case CHECKER_TYPES.upperCaseLatinSymbol:
          if (!passwordChecker.checkAZHigh(this.value as string)) { this.errorMessage += 'Должен быть как минимум 1 символ A-Z в верхнем регистре.' }
          break
        case CHECKER_TYPES.specialSymbol:
          if (!passwordChecker.checkSpecialSymbol(this.value as string)) { this.errorMessage += 'Должен быть как минимум 1 специальный символ.' }
          break
        case CHECKER_TYPES.maxValue:
          if (this.type === 'float' && !numberChecker.checkMaxValue(this.value as number, parseFloat(param))) { this.errorMessage += `Число должно быть меньше или равно ${param}.` }
          if (this.type === 'int' && !numberChecker.checkMaxValue(this.value as number, parseInt(param))) { this.errorMessage += `Число должно быть меньше или равно ${param}.` }
          break
        case CHECKER_TYPES.minValue:
          if (this.type === 'float' && !numberChecker.checkMinValue(this.value as number, parseFloat(param))) { this.errorMessage += `Число должно быть больше или равно ${param}.` }
          if (this.type === 'int' && !numberChecker.checkMinValue(this.value as number, parseInt(param))) { this.errorMessage += `Число должно быть больше или равно ${param}.` }
          break
        case CHECKER_TYPES.float:
          this.fieldValue = String(this.value).replace(/,/g, '.')
          if (parseFloat(this.value as string).toString() !== String(this.value)) { this.errorMessage += 'Формат числа неверный.' }
          break
        case CHECKER_TYPES.int:
          if (parseInt(this.value as string).toString() !== String(this.value)) { this.errorMessage += 'Формат числа неверный. Пожалуйста, введите целое.' }
          break
      }
    })

    return !this.errorMessage
  }

  isEmpty (): boolean {
    return false
  }

  // Очищает значение поля. Для полей с проверкой digitsAll используется 0,
  // для остальных - null
  clear (): void {
    const hasDigitsAllChecker = this.checkers?.digitsAll !== undefined

    this.value = hasDigitsAllChecker ? 0 : null
  }

  dispatch (type: EnumEventTypes, opts?: { silent?: boolean }): void {
    dispatchFieldEvent(this, this._uid, type, opts)
  }

  refreshValue (_field: BaseFieldClass): void {
    // обновляем только значение поля,
    // readonly и варианты значений для списковых полей. Остальную
    // конфигурацию текущего поля сохраняем.
    if (this.name !== _field.name) return

    const valueIsChanged = !isEqual(this.fieldValue, _field.fieldValue)

    if (_field.possibleValues !== null && typeof _field.possibleValues === 'object' && Object.values(_field.possibleValues).length) {
      this.possibleValues = _field.possibleValues
    }

    this.fieldValue = _field.fieldValue
    this.readonly = _field.readonly
    this._disabled = this.readonly

    if (typeof this.value === 'string' && this.type === 'listInt') {
      this.value = this.prepareValue(this.value)
    }

    if (valueIsChanged) {
      this.dispatch(EnumEventTypes.CHANGE)
    }
  }

  refreshField (_field: BaseFieldClass): void {
    // Имена полей разные, игнорируем обновление поля
    if (this.name !== _field.name) return

    let valueIsChanged = false

    for (const key in _field) {
      const fieldKey = key as keyof BaseFieldClass

      if (fieldKey === 'possibleValues') {
      // Почему то бывает, что possibleValues приходит как пустой объект, в таком случае оставим старое значение
        if (_field[fieldKey] !== null && typeof _field[fieldKey] === 'object' && Object.values(_field[fieldKey]).length) {
          this[fieldKey] = _field[fieldKey]
        }
      } else if (fieldKey === 'fieldValue') {
        if (!isEqual(this.fieldValue, _field.fieldValue)) {
          valueIsChanged = true
        }
        this[fieldKey] = _field[fieldKey]
      } else if (key !== '_widget' && fieldKey !== 'initialValue' && fieldKey !== '_uid') {
        /*
          Конфигурацию виджета не заменяем!
          _fields будет содержать пустое поле _widget, так как
          конфигурация запрашивается отдельно в рамках компонента виджета;
          _uid - это поле с уникальным идентификатором, его не обновляем
          initialValue - это поле с установленным начальным значением, его не обновляем
        */
        (this[fieldKey] as unknown) = _field[fieldKey]
      }
    }

    if (typeof this.value === 'string' && this.type === 'listInt') {
      this.value = this.prepareValue(this.value)
    }

    if (valueIsChanged) {
      // Поле обновилось - вызовем событие CHANGE
      this.dispatch(EnumEventTypes.CHANGE)
    }
  }
}
